import React, { useEffect, useState, useRef, Component } from 'react';
import { MessageSquareIcon, SendIcon, SearchIcon, ArrowLeftIcon, MoreVerticalIcon, PaperclipIcon, SmileIcon, CheckIcon, CheckCheckIcon, PhoneIcon, VideoIcon, InfoIcon, ImageIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: Date;
  read: boolean;
}
interface Conversation {
  id: string;
  with: string;
  avatar: string;
  lastMessage: string;
  time: string;
  unread: boolean;
  online: boolean;
  messages: Message[];
}
const mockConversations: Conversation[] = [{
  id: '1',
  with: 'Elena Richards',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  lastMessage: 'Thank you for your feedback on The Silent Echo!',
  time: '2:34 PM',
  unread: true,
  online: true,
  messages: [{
    id: 'm1',
    senderId: 'elena',
    text: 'Hi! I saw you left a review on The Silent Echo. Thank you so much!',
    timestamp: new Date('2024-01-15T14:20:00'),
    read: true
  }, {
    id: 'm2',
    senderId: 'me',
    text: 'Of course! I absolutely loved it. The plot twists were incredible.',
    timestamp: new Date('2024-01-15T14:25:00'),
    read: true
  }, {
    id: 'm3',
    senderId: 'elena',
    text: 'That means so much to me! The twist in chapter 12 was actually inspired by a real event.',
    timestamp: new Date('2024-01-15T14:28:00'),
    read: true
  }, {
    id: 'm4',
    senderId: 'me',
    text: 'No way! That makes it even more impressive. When is the sequel coming out?',
    timestamp: new Date('2024-01-15T14:30:00'),
    read: true
  }, {
    id: 'm5',
    senderId: 'elena',
    text: 'Thank you for your feedback on The Silent Echo!',
    timestamp: new Date('2024-01-15T14:34:00'),
    read: false
  }]
}, {
  id: '2',
  with: 'Marcus Chen',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  lastMessage: 'The book signing event is confirmed for next week.',
  time: 'Yesterday',
  unread: false,
  online: false,
  messages: [{
    id: 'm1',
    senderId: 'marcus',
    text: 'Hey! Just wanted to confirm the book signing event details.',
    timestamp: new Date('2024-01-14T10:00:00'),
    read: true
  }, {
    id: 'm2',
    senderId: 'me',
    text: 'Sure! What do you need to know?',
    timestamp: new Date('2024-01-14T10:05:00'),
    read: true
  }, {
    id: 'm3',
    senderId: 'marcus',
    text: 'The book signing event is confirmed for next week.',
    timestamp: new Date('2024-01-14T10:10:00'),
    read: true
  }]
}, {
  id: '3',
  with: 'Sarah Johnson',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  lastMessage: 'Would you be interested in a collaboration?',
  time: 'Mon',
  unread: false,
  online: true,
  messages: [{
    id: 'm1',
    senderId: 'sarah',
    text: 'Hi there! I love your work.',
    timestamp: new Date('2024-01-13T15:00:00'),
    read: true
  }, {
    id: 'm2',
    senderId: 'sarah',
    text: 'Would you be interested in a collaboration?',
    timestamp: new Date('2024-01-13T15:02:00'),
    read: true
  }]
}, {
  id: '4',
  with: 'Literary Apparel Co.',
  avatar: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  lastMessage: 'Your merch order has shipped!',
  time: 'Jan 10',
  unread: false,
  online: false,
  messages: [{
    id: 'm1',
    senderId: 'vendor',
    text: 'Thank you for your order!',
    timestamp: new Date('2024-01-10T09:00:00'),
    read: true
  }, {
    id: 'm2',
    senderId: 'vendor',
    text: 'Your merch order has shipped!',
    timestamp: new Date('2024-01-10T14:00:00'),
    read: true
  }]
}];
const Messages = () => {
  const {
    darkMode
  } = useDarkMode();
  const [conversations] = useState<Conversation[]>(mockConversations);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [selectedConversation?.messages]);
  const filteredConversations = conversations.filter(conv => conv.with.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    // In a real app, this would send to an API
    console.log('Sending message:', newMessage);
    setNewMessage('');
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  // Conversation List Component
  const ConversationList = () => <div className={`flex flex-col h-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-9888" data-mp="22nu229kydt18">
      {/* Header */}
      <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-9889" data-mp="1yna79cnur1i9">
        <h1 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9890" data-mp="2cnb6yosds2w5">
          Messages
        </h1>
        <div className="relative" data-id="element-9891" data-mp="27p9pu9eimdnb">
          <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9892" data-mp="2lmg35oggzk55" />

          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search conversations..." className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'}`} data-id="element-9893" data-mp="2hja2qxg1gn6l" />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto" data-id="element-9894" data-mp="3nz95hvg35t1h">
        {filteredConversations.length === 0 ? <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9895" data-mp="3nwwnjiabviof">
            <MessageSquareIcon className="h-12 w-12 mx-auto mb-3 opacity-50" data-id="element-9896" data-mp="3eyb29fdh1pjy" />
            <p data-id="element-9897" data-mp="3f0dw6ahqw2ty">No conversations found</p>
          </div> : filteredConversations.map(conversation => <button key={conversation.id} onClick={() => setSelectedConversation(conversation)} className={`w-full p-4 flex items-center gap-3 border-b transition-colors ${selectedConversation?.id === conversation.id ? darkMode ? 'bg-indigo-900/30 border-gray-700' : 'bg-indigo-50 border-gray-200' : darkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'} ${conversation.unread ? 'font-medium' : ''}`} data-id="element-9898" data-mp="350wx4bx00n6y">
              <div className="relative flex-shrink-0" data-id="element-9899" data-mp="31sf32914urok">
                <img src={conversation.avatar} alt={conversation.with} className="w-12 h-12 rounded-full object-cover" data-id="element-9900" data-mp="31r3wy2k7t7ua" />

                {conversation.online && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" data-id="element-9901" data-mp="2nse10q94te7i" />}
              </div>
              <div className="flex-1 min-w-0 text-left" data-id="element-9902" data-mp="2iuvsh0z11101">
                <div className="flex items-center justify-between mb-1" data-id="element-9903" data-mp="2e1j48ry9ew7t">
                  <span className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9904" data-mp="2a07n7atgbjb8">
                    {conversation.with}
                  </span>
                  <span className={`text-xs flex-shrink-0 ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9905" data-mp="202t9vk2tran5">
                    {conversation.time}
                  </span>
                </div>
                <p className={`text-sm truncate ${conversation.unread ? darkMode ? 'text-white' : 'text-gray-900' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-9906" data-mp="1kz9u1ko82di8">
                  {conversation.lastMessage}
                </p>
              </div>
              {conversation.unread && <span className="w-2.5 h-2.5 bg-indigo-500 rounded-full flex-shrink-0" data-id="element-9907" data-mp="1291vzgzdqd8w" />}
            </button>)}
      </div>
    </div>;
  // Message Thread Component
  const MessageThread = () => {
    if (!selectedConversation) {
      return <div className={`flex-1 flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-9908" data-mp="1c5dcyk9wzr9o">
          <div className="text-center" data-id="element-9909" data-mp="jj2dxe1yhyez">
            <MessageSquareIcon className={`h-16 w-16 mx-auto mb-4 ${darkMode ? 'text-gray-700' : 'text-gray-300'}`} data-id="element-9910" data-mp="ji11b55al3v5" />

            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9911" data-mp="1sg1c2a0xybi2">
              Select a conversation
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-9912" data-mp="1xgp2athciob8">
              Choose a conversation from the list to start messaging
            </p>
          </div>
        </div>;
    }
    return <div className={`flex flex-col h-full ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-9913" data-mp="19qcnqzrlhtbo">
        {/* Thread Header */}
        <div className={`p-4 border-b flex items-center gap-3 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-9914" data-mp="1ema2j1n7mrmc">
          {isMobileView && <button onClick={() => setSelectedConversation(null)} className={`p-2 -ml-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} data-id="element-9915" data-mp="1nhzjmyhmkztx">
              <ArrowLeftIcon className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9916" data-mp="uy0zfwk4p182" />
            </button>}
          <div className="relative" data-id="element-9917" data-mp="ij2wcgmaafum">
            <img src={selectedConversation.avatar} alt={selectedConversation.with} className="w-10 h-10 rounded-full object-cover" data-id="element-9918" data-mp="ifynv71scxy5" />

            {selectedConversation.online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" data-id="element-9919" data-mp="4i9zvqcf9aov" />}
          </div>
          <div className="flex-1 min-w-0" data-id="element-9920" data-mp="3qxkyy2tgh36u">
            <h2 className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9921" data-mp="3qymjqz09x41r">
              {selectedConversation.with}
            </h2>
            <p className={`text-xs ${selectedConversation.online ? 'text-green-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9922" data-mp="3hx6cv18o3k2a">
              {selectedConversation.online ? 'Online' : 'Offline'}
            </p>
          </div>
          <div className="flex items-center gap-1" data-id="element-9923" data-mp="33ckby7tmw8c8">
            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-9924" data-mp="38ac7dbthmcl6">
              <PhoneIcon className="h-5 w-5" data-id="element-9925" data-mp="2yabhf7144y80" />
            </button>
            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-9926" data-mp="2ycxxjv22km1u">
              <VideoIcon className="h-5 w-5" data-id="element-9927" data-mp="3qz0pjq0l63t9" />
            </button>
            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-9928" data-mp="3c9urw6heclnh">
              <InfoIcon className="h-5 w-5" data-id="element-9929" data-mp="8tnom12w8qf8" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-9930" data-mp="dlxg3ljnpyjm">
          {selectedConversation.messages.map((message, index) => {
          const isMe = message.senderId === 'me';
          const showAvatar = !isMe && (index === 0 || selectedConversation.messages[index - 1].senderId === 'me');
          return <div key={message.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-9931" data-mp="2329m8a5p8822" data-mpi={index}>
                {!isMe && <div className="w-8 flex-shrink-0" data-id="element-9932" data-mp="1adltjtimegeh">
                    {showAvatar && <img src={selectedConversation.avatar} alt="" className="w-8 h-8 rounded-full object-cover" data-id="element-9933" data-mp="1od52goj99vru" />}
                  </div>}
                <div className={`max-w-[70%] ${isMe ? 'order-1' : ''}`} data-id="element-9934" data-mp="2zqgzogkifg51">
                  <div className={`px-4 py-2 rounded-2xl ${isMe ? 'bg-indigo-500 text-white rounded-br-md' : darkMode ? 'bg-gray-800 text-gray-100 rounded-bl-md' : 'bg-white text-gray-900 rounded-bl-md shadow-sm'}`} data-id="element-9935" data-mp="2vpwzt58jxyf7">
                    <p className="text-sm" data-id="element-9936" data-mp="3wfb1lpemqqn">{message.text}</p>
                  </div>
                  <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-9937" data-mp="3yi98t0pnq6g">
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9938" data-mp="dtaac6ti4v5z">
                      {formatMessageTime(message.timestamp)}
                    </span>
                    {isMe && (message.read ? <CheckCheckIcon className="h-3.5 w-3.5 text-indigo-400" data-id="element-9939" data-mp="rkos6aboaqbg" /> : <CheckIcon className={`h-3.5 w-3.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9940" data-mp="wm4gqcd27eyl" />)}
                  </div>
                </div>
              </div>;
        })}
          <div ref={messagesEndRef} data-id="element-9941" data-mp="2h2c5l8qdcppr" />
        </div>

        {/* Message Input */}
        <div className={`p-4 border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-9942" data-mp="2c3ishhezv0q4">
          <div className={`flex items-end gap-2 rounded-2xl border p-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`} data-id="element-9943" data-mp="275r24rtzzmo1">
            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`} data-id="element-9944" data-mp="2zstm8exxfuur">
              <PaperclipIcon className="h-5 w-5" data-id="element-9945" data-mp="2q3juj06zwe76" />
            </button>
            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`} data-id="element-9946" data-mp="2q3a3rae4m30f">
              <ImageIcon className="h-5 w-5" data-id="element-9947" data-mp="3irfovy93du0s" />
            </button>
            <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type a message..." rows={1} className={`flex-1 resize-none bg-transparent border-0 focus:ring-0 focus:outline-none py-2 px-1 max-h-32 ${darkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`} style={{
            minHeight: '40px'
          }} data-id="element-9948" data-mp="1gu5hfmcv1ccp" />

            <button className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`} data-id="element-9949" data-mp="126keptjwdf5b">
              <SmileIcon className="h-5 w-5" data-id="element-9950" data-mp="t4b5d70d7rti" />
            </button>
            <button onClick={handleSendMessage} disabled={!newMessage.trim()} className={`p-2 rounded-lg transition-colors ${newMessage.trim() ? 'bg-indigo-500 text-white hover:bg-indigo-600' : darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-400'}`} data-id="element-9951" data-mp="2ik14wb38xmpm">
              <SendIcon className="h-5 w-5" data-id="element-9952" data-mp="6x7c8mvtwz3i" />
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-9953" data-mp="lmmypqobuide">
      <div className="max-w-7xl mx-auto h-[calc(100vh-4rem)] md:h-[calc(100vh-8rem)] md:py-6" data-id="element-9954" data-mp="gulhdqcay1gi">
        <div className={`h-full md:rounded-xl md:shadow-lg overflow-hidden flex ${darkMode ? 'md:border md:border-gray-700' : 'md:border md:border-gray-200'}`} data-id="element-9955" data-mp="ulqiyxz8d3th">
          {/* Desktop: Side-by-side layout */}
          {/* Mobile: Show list or thread based on selection */}
          {isMobileView ? selectedConversation ? <div className="w-full" data-id="element-9956" data-mp="zjicfwfiwxzs">
                <MessageThread data-id="element-9957" data-mp="36b10tedlxm2u" />
              </div> : <div className="w-full" data-id="element-9958" data-mp="31cprd4r85dl5">
                <ConversationList data-id="element-9959" data-mp="31drcy35v9ih4" />
              </div> : <>
              <div className={`w-80 flex-shrink-0 border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-9960" data-mp="2fiissgwd4izr">
                <ConversationList data-id="element-9961" data-mp="2kgagsztvzkyo" />
              </div>
              <div className="flex-1" data-id="element-9962" data-mp="2paestxi0ej6m">
                <MessageThread data-id="element-9963" data-mp="2pbgban1cu7yd" />
              </div>
            </>}
        </div>
      </div>
    </div>;
};
export default Messages;